import { motion } from "framer-motion"
import { useNavigate } from "react-router-dom"
import { Check, ShoppingBag } from "lucide-react"
import { useTranslation } from "react-i18next"

const OrderSuccessPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleContinueShopping = () => {
    navigate("/")
  }

  return (
    <div className="flex-grow bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 flex items-center justify-center p-4 dark:from-transparent dark:via-transparent dark:to-transparent section">
      <div className="w-full max-w-sm mx-auto text-center">
        {/* Success Icon with Animation */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 15,
            delay: 0.2,
          }}
          className="flex justify-center mb-10"
        >
          <div className="relative mb-4">
            {/* Outer glow ring */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1.5, opacity: 0.4 }}
              transition={{
                duration: 1.5,
                repeat: Number.POSITIVE_INFINITY,
                repeatType: "reverse",
                ease: "easeInOut",
              }}
              className="absolute inset-0 bg-primary-100 rounded-full blur-xl"
            />

            {/* Main success icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 20,
                delay: 0.4,
              }}
              className="relative w-24 h-24 bg-gradient-to-br from-primary-100 to-primary-600 rounded-full flex items-center justify-center shadow-2xl shadow-purple-500/50"
            >
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.3 }}
              >
                <Check className="w-12 h-12 text-white stroke-[3]" />
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mb-10"
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">{t("cart.orderSuccessPage.title")}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-base leading-relaxed px-2">
            {t("cart.orderSuccessPage.message")}
          </p>
        </motion.div>

        {/* Shop More Button - Made Smaller */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleContinueShopping}
            className="relative overflow-hidden bg-primary-100 hover:bg-primary-600 text-white py-3.5 px-8 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl hover:shadow-purple-500/25 transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
          >
            {/* Button shine effect */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: "100%" }}
              transition={{
                duration: 2,
                repeat: Number.POSITIVE_INFINITY,
                repeatDelay: 3,
                ease: "easeInOut",
              }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
            />

            <ShoppingBag className="w-5 h-5 relative z-10" />
            <span className="relative z-10">{t("cart.orderSuccessPage.shopMore")}</span>
          </motion.button>
        </motion.div>

        {/* Floating Particles Animation */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              initial={{
                opacity: 0,
                y: 100,
                x: Math.random() * (typeof window !== "undefined" ? window.innerWidth : 400),
              }}
              animate={{
                opacity: [0, 1, 0],
                y: -100,
                x: Math.random() * (typeof window !== "undefined" ? window.innerWidth : 400),
              }}
              transition={{
                duration: 3,
                delay: 1.5 + i * 0.15,
                repeat: Number.POSITIVE_INFINITY,
                repeatDelay: 2,
              }}
              className="absolute w-1.5 h-1.5 bg-primary-100 rounded-full"
            />
          ))}
        </div>

        {/* Enhanced grocery-themed decoration */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.08 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute inset-0 pointer-events-none"
        >
          <motion.div
            animate={{
              rotate: [0, 5, -5, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 4,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
            }}
            className="absolute top-16 left-8 text-4xl"
          >
            🥕
          </motion.div>
          <motion.div
            animate={{
              rotate: [0, -5, 5, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 3.5,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
              delay: 0.5,
            }}
            className="absolute top-24 right-12 text-3xl"
          >
            🍎
          </motion.div>
          <motion.div
            animate={{
              rotate: [0, 3, -3, 0],
              scale: [1, 1.08, 1],
            }}
            transition={{
              duration: 4.5,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
              delay: 1,
            }}
            className="absolute bottom-32 left-16 text-3xl"
          >
            🥬
          </motion.div>
          <motion.div
            animate={{
              rotate: [0, -4, 4, 0],
              scale: [1, 1.06, 1],
            }}
            transition={{
              duration: 3.8,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
              delay: 1.5,
            }}
            className="absolute bottom-24 right-8 text-3xl"
          >
            🍌
          </motion.div>
          <motion.div
            animate={{
              rotate: [0, 6, -6, 0],
              scale: [1, 1.04, 1],
            }}
            transition={{
              duration: 4.2,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
              delay: 0.8,
            }}
            className="absolute top-40 left-1/2 transform -translate-x-1/2 text-2xl"
          >
            🥦
          </motion.div>
        </motion.div>

        {/* Subtle background pattern */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary-100/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/3 right-1/4 w-24 h-24 bg-purple-400/5 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 right-1/3 w-16 h-16 bg-violet-400/5 rounded-full blur-xl animate-pulse delay-500"></div>
        </div>
      </div>
    </div>
  )
}

export default OrderSuccessPage
